import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import Icon from 'components/AppIcon';

const seasonalData = [
  { month: 'Jan', index: 82, label: 'Low', events: 'New Year recovery' },
  { month: 'Feb', index: 88, label: 'Low', events: 'Valentine promos' },
  { month: 'Mar', index: 95, label: 'Normal', events: 'Summer prep' },
  { month: 'Apr', index: 118, label: 'High', events: 'Holy Week, Summer' },
  { month: 'May', index: 135, label: 'Peak', events: 'Fiesta season' },
  { month: 'Jun', index: 128, label: 'Peak', events: 'School opening' },
  { month: 'Jul', index: 105, label: 'Normal', events: 'Mid-year' },
  { month: 'Aug', index: 110, label: 'High', events: 'Buwan ng Wika' },
  { month: 'Sep', index: 108, label: 'Normal', events: 'Ber months start' },
  { month: 'Oct', index: 122, label: 'High', events: 'Halloween, Undas' },
  { month: 'Nov', index: 145, label: 'Peak', events: 'Undas, Pasko prep' },
  { month: 'Dec', index: 168, label: 'Peak', events: 'Christmas, Noche Buena' },
];

const INDEX_COLORS = {
  Low: '#94A3B8',
  Normal: '#DC2626',
  High: '#D4AF37',
  Peak: '#059669',
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = seasonalData?.find((s) => s?.month === label);
  return (
    <div className="bg-card border border-border rounded-lg p-3 shadow-lg text-xs font-caption min-w-[160px]">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      <p className="text-muted-foreground">Demand Index: <strong className="text-foreground">{payload?.[0]?.value}</strong></p>
      <p className="text-muted-foreground">Level: <strong style={{ color: INDEX_COLORS?.[d?.label] }}>{d?.label}</strong></p>
      <p className="text-muted-foreground mt-1">{d?.events}</p>
    </div>
  );
};

export default function SeasonalDemandChart() {
  const [highlighted, setHighlighted] = useState(null);

  return (
    <div className="card p-4 lg:p-6 chart-panel-depth">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-heading font-semibold text-lg md:text-xl" style={{ color: '#0F172A', letterSpacing: '0.01em' }}>Seasonal Demand Index</h3>
          <p className="font-caption text-sm mt-0.5" style={{ color: '#475569' }}>AI-predicted demand patterns based on 3-year historical data</p>
        </div>
        <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
          <Icon name="Calendar" size={13} color="var(--color-warning)" strokeWidth={2} />
          <span className="font-caption text-xs font-medium text-amber-700">2026</span>
        </div>
      </div>
      <div className="w-full h-48 md:h-56">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={seasonalData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }} onMouseLeave={() => setHighlighted(null)}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" strokeOpacity={0.6} vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 13, fontFamily: 'var(--font-caption)', fill: '#374151', fontWeight: 600 }} axisLine={false} tickLine={false} />
            <YAxis domain={[60, 180]} tick={{ fontSize: 13, fontFamily: 'var(--font-data)', fill: '#374151', fontWeight: 600 }} axisLine={false} tickLine={false} width={35} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="index" radius={[4, 4, 0, 0]} onMouseEnter={(_, i) => setHighlighted(i)}
              style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.12))' }}
            >
              {seasonalData?.map((entry, i) => (
                <Cell
                  key={i}
                  fill={INDEX_COLORS?.[entry?.label]}
                  opacity={highlighted === null || highlighted === i ? 1 : 0.5}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-border">
        {Object.entries(INDEX_COLORS)?.map(([label, color]) => (
          <div key={label} className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: color }} />
            <span className="font-caption text-xs font-medium" style={{ color: '#475569' }}>{label}</span>
          </div>
        ))}
        <div className="ml-auto flex items-center gap-1.5">
          <Icon name="Info" size={12} color="#475569" strokeWidth={2} />
          <span className="font-caption text-xs" style={{ color: '#475569' }}>Base = 100 (average month)</span>
        </div>
      </div>
    </div>
  );
}